import { forwardRef, Ref, ChangeEvent } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FieldError, Controller } from 'react-hook-form'
import flags from 'react-phone-number-input/flags'
import { FormError, Label } from 'shared/atoms'
import 'react-phone-number-input/style.css'
import PhoneInput, { Country, isValidPhoneNumber } from 'react-phone-number-input'
import { WithClassName } from 'shared/types/general.types'
import { cn } from 'utils/style'

type InputProps = WithClassName<{
	id: string
	label?: string
	name: string
	type?: string
	icon?: any
	error?: FieldError | undefined
	placeholder?: string
	disabled?: boolean
	ref?: Ref<HTMLInputElement>
	onChange?: (e: ChangeEvent) => void
	step?: string
	control?: any
	defaultCountry?: Country
	variant?: 'default' | 'form'
}>

export const PhoneNumberInput = forwardRef(
	(
		{
			id,
			label,
			name,
			icon,
			placeholder,
			disabled,
			className,
			type = 'text',
			error,
			step,
			control,
			defaultCountry,
			variant = 'default'
		}: InputProps,
		_ref
	) => {
		const isForm = variant === 'form'

		return (
			<div className={cn('flex flex-col', isForm ? 'gap-y-1.5' : 'gap-y-2.5', className)}>
				<Label htmlFor={id} text={label} />
				<div
					className={cn(
						'relative flex items-center',
						isForm
							? 'h-10 overflow-hidden rounded-md border border-border bg-background text-sm text-foreground transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 [&_.PhoneInputCountry]:m-0 [&_.PhoneInputCountry]:gap-1.5 [&_.PhoneInputCountry]:border-r [&_.PhoneInputCountry]:border-border [&_.PhoneInputCountry]:bg-muted [&_.PhoneInputCountry]:px-3 [&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:min-w-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:px-3 [&_.PhoneInputInput]:outline-none [&_.PhoneInput]:h-full [&_.PhoneInput]:w-full'
							: 'gap-2.5 rounded-lg shadow-[0_0_4px_rgba(0,0,0,0.08)]',
						isForm &&
							error &&
							'border-danger focus-within:border-danger focus-within:ring-danger/20'
					)}>
					{icon && (
						<>
							{typeof icon === 'string' ? (
								<div className="absolute bottom-0 left-0 top-0 flex w-[39px] items-center justify-center rounded-l-lg bg-mercury text-sm text-dustyGray">
									{icon}
								</div>
							) : (
								<div className="absolute bottom-0 left-0 top-0 flex w-[39px] items-center justify-center rounded-l-lg bg-mercury text-sm text-dustyGray">
									<FontAwesomeIcon icon={icon} />
								</div>
							)}
						</>
					)}

					<Controller
						name={name}
						rules={{
							validate: (value) => isValidPhoneNumber(value)
						}}
						control={control}
						render={({ field: { onChange, value } }) => (
							<PhoneInput
								type={type}
								id={id}
								placeholder={placeholder}
								onChange={onChange}
								value={value}
								flags={flags}
								disabled={disabled}
								step={step}
								defaultCountry={defaultCountry}
								countryCallingCodeEditable={false}
							/>
						)}
					/>
				</div>
				{isForm ? (
					<FormError text={error?.message} />
				) : (
					error && <span className="text-2xs font-bold text-red">{error.message}</span>
				)}
			</div>
		)
	}
)
